import { Disclosure } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dumraLogo from '../assets/dumra-logo.png';
import VillageInfo from '../components/VillageInfo';
import WhatsAppFloat from '../components/WhatsAppFloat';
import BackToTopButton from '../components/BackToTopButton';
import { useTheme } from '../components/ThemeProvider';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Events', href: '/events' },
  { name: 'News', href: '/news' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Stories', href: '/stories' },
  { name: 'Leaders', href: '/leaders' },
  { name: 'Emergency', href: '/emergency' },
  { name: 'Feedback', href: '/feedback' },
  { name: 'Contact', href: '/contact' },
];

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { dark, toggle } = useTheme();

  const setLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Skip to content */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg">
        {t('Skip to content')}
      </a>

      <Disclosure as="nav" className="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-30 border-b border-gray-100 dark:border-gray-700 print:hidden">
        {({ open }) => (
          <>
            <div className="w-full px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 items-center justify-between w-full">
                {/* Logo and Nav */}
                <div className="flex items-center gap-8 w-full">
                  <Link to="/" className="flex items-center gap-2 shrink-0">
                    <img
                      className="h-9 w-auto"
                      src={dumraLogo}
                      alt="Rampur Dumra"
                    />
                  </Link>
                  <div className="hidden lg:flex gap-0.5 flex-1">
                    {navigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`inline-flex items-center px-2.5 xl:px-3 pt-1 text-sm xl:text-base font-medium transition-all duration-200 whitespace-nowrap rounded-md py-1.5 ${
                          location.pathname === item.href
                            ? 'bg-primary-50 text-primary-700 font-semibold'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-primary-600'
                        }`}
                      >
                        {t(item.name)}
                      </Link>
                    ))}
                  </div>
                </div>
                {/* Language Switcher + Dark Mode */}
                <div className="hidden lg:flex items-center ml-4 gap-2">
                  <button
                    onClick={toggle}
                    className="p-2 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400 transition-colors"
                    aria-label="Toggle dark mode"
                  >
                    {dark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
                  </button>
                  <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-full p-1">
                    <button
                      onClick={() => setLanguage('en')}
                      className={`px-3 py-1 rounded-full text-sm font-semibold focus:outline-none transition-all duration-200 ${
                        i18n.language === 'en'
                          ? 'bg-primary-600 text-white shadow-md'
                          : 'text-gray-500 hover:text-primary-600'
                      }`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => setLanguage('hi')}
                      className={`px-3 py-1 rounded-full text-sm font-semibold focus:outline-none transition-all duration-200 ${
                        i18n.language === 'hi'
                          ? 'bg-primary-600 text-white shadow-md'
                          : 'text-gray-500 hover:text-primary-600'
                      }`}
                    >
                      HI
                    </button>
                  </div>
                </div>
                {/* Mobile menu button */}
                <div className="-mr-2 flex items-center lg:hidden">
                  <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500">
                    <span className="sr-only">Open main menu</span>
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
                </div>
              </div>
            </div>

            <Disclosure.Panel className="lg:hidden">
              <div className="space-y-1 pb-3 pt-2">
                {navigation.map((item) => (
                  <Disclosure.Button
                    key={item.name}
                    as={Link}
                    to={item.href}
                    className={`block border-l-4 py-2 pl-3 pr-4 text-base font-medium ${
                      location.pathname === item.href
                        ? 'border-primary-500 bg-primary-50 text-primary-700'
                        : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                    }`}
                  >
                    {t(item.name)}
                  </Disclosure.Button>
                ))}
              </div>
              <div className="border-t border-gray-200 pb-3 pt-4">
                <div className="flex items-center px-4">
                  <div className="flex items-center bg-gray-100 rounded-full p-1 w-full justify-center">
                    <button
                      onClick={() => setLanguage('en')}
                      className={`px-3 py-1 rounded-full text-sm font-semibold focus:outline-none transition-all duration-200 ${
                        i18n.language === 'en'
                          ? 'bg-primary-600 text-white shadow-md'
                          : 'text-gray-500 hover:text-primary-600'
                      }`}
                    >
                      EN
                    </button>
                    <button
                      onClick={() => setLanguage('hi')}
                      className={`px-3 py-1 rounded-full text-sm font-semibold focus:outline-none transition-all duration-200 ${
                        i18n.language === 'hi'
                          ? 'bg-primary-600 text-white shadow-md'
                          : 'text-gray-500 hover:text-primary-600'
                      }`}
                    >
                      HI
                    </button>
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <main id="main-content">{children}</main>

      <VillageInfo />

      {/* Footer */}
      <footer className="bg-gray-900 text-white print:hidden">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* About */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-accent-400">{t('About Rampur Dumra')}</h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                {t('Footer About Description')}
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-accent-400">{t('Quick Links')}</h3>
              <ul className="space-y-2">
                {[
                  { name: 'Gallery', href: '/gallery' },
                  { name: 'Stories', href: '/stories' },
                  { name: 'Feedback', href: '/feedback' },
                  { name: 'Emergency', href: '/emergency' },
                  { name: 'Contact', href: '/contact' },
                ].map((link) => (
                  <li key={link.name}>
                    <Link to={link.href} className="text-gray-400 hover:text-accent-400 text-sm transition-colors">
                      {t(link.name)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Social Media */}
            <div>
              <h3 className="text-lg font-bold mb-4 text-accent-400">{t('Follow Us')}</h3>
              <div className="flex gap-4">
                <a href="https://www.facebook.com/rampurdumra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-white transition-all" title="Facebook">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://www.instagram.com/rampurdumra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-pink-600 hover:text-white transition-all" title="Instagram">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
                <a href="https://www.youtube.com/@rampurdumra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-red-600 hover:text-white transition-all" title="YouTube">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
                <a href="https://chat.whatsapp.com/rampurdumra" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center text-gray-400 hover:bg-green-600 hover:text-white transition-all" title="WhatsApp">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                </a>
              </div>
              <p className="mt-4 text-gray-400 text-sm">
                Rampur Dumra, Dist Patna, Bihar 803301
              </p>
            </div>
          </div>

          <div className="mt-10 border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-500">
              &copy; {new Date().getFullYear()} Rampur Dumra Village Portal. {t('All Rights Reserved')}.
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Buttons */}
      <BackToTopButton />
      <WhatsAppFloat />
    </div>
  );
}
